import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "jspdf-autotable";
import jsPDF from "jspdf";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { configure, ZipWriter, BlobWriter, TextReader } from "@zip.js/zip.js";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Paper,
  Tabs,
  Tab,
  Box,
  useMediaQuery,
  Select,
  MenuItem,
  Skeleton,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  RadioGroup,
  Radio,
  IconButton,
  Avatar,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import { Add, Edit, ExpandMore } from "@mui/icons-material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import FeedIcon from "@mui/icons-material/Feed";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import GetAppIcon from "@mui/icons-material/GetApp";

import CommonButton from "../../Components/InputComponents/CommonButton";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import { decryptId, encryptId } from "../../Components/CryptoComponent";
import ProcessFlowViewer from "./ProcessFlowViewer";
import {
  errorNotify,
  infoNotify,
  successNotify,
} from "../../Components/ToastComponent/ToastNotification";
import TextInput from "../../Components/InputComponents/TextInput";
import TextAreaInput from "../../Components/InputComponents/TextAreaInput";
import SelectInput from "../../Components/InputComponents/SelectInput";

const CandidateProfile = () => {
  const getIcon = (status, color) => {
    switch (status) {
      case "Completed":
        return <CheckCircleIcon sx={{ color, fontSize: 24 }} />;
      case "In Process":
        return <AccessTimeIcon sx={{ color, fontSize: 24 }} />;
      case "Draft":
        return <FeedIcon sx={{ color, fontSize: 24 }} />;

      default:
        return null;
    }
  };

  const documentSubmission = [
    {
      title: "Passport Copy",
      date: "2025-03-01",
      status: "Submitted",
    },
    {
      title: "Medical Report",
      date: "2025-03-05",
      status: "Pending",
    },
    {
      title: "Visa Application",
      date: "2025-03-10",
      status: "Submitted",
    },
  ];

  const handlePrint = () => {
    const pdf = new jsPDF("p", "mm", "a4");
    let yPosition = 30;

    const currentDateTime = new Date().toLocaleString();

    // Add Header
    const addHeader = () => {
      pdf.setFontSize(10);
      pdf.setFont("Poppins", "bold");
      pdf.text("Signature Work Abraod", 15, 15);
      pdf.setLineWidth(0.1); // Set the line thickness to a very thin value (you can try values like 0.01 for even thinner lines)
      pdf.line(10, 18, 200, 18); // Draw the line with the specified thickness
    };

    // Add Footer
    const addFooter = (pageNumber, totalPages) => {
      pdf.setFontSize(10);
      pdf.setFont("Poppins", "normal");
      pdf.text(`Page ${pageNumber} of ${totalPages}`, 90, 290);
      pdf.setFontSize(9);
      pdf.text(`Printed on: ${currentDateTime}`, 15, 295);
    };

    // Add Title (Main Heading Centered, Others Left-Aligned)
    const addTitle = (title, fontSize = 16, isCentered = false) => {
      pdf.setFontSize(fontSize);
      pdf.setFont("Poppins", "bold");
      const pageWidth = pdf.internal.pageSize.getWidth();
      if (isCentered) {
        const textWidth = pdf.getTextWidth(title);
        pdf.text(title, (pageWidth - textWidth) / 2, yPosition);
      } else {
        pdf.text(title, 15, yPosition);
      }
      yPosition += 12;
    };

    // Add Content with Highlighted Labels
    const addContent = (content) => {
      pdf.setFontSize(12);
      pdf.setFont("Poppins", "normal");
      content.forEach(([label, value]) => {
        if (yPosition > 270) {
          pdf.addPage();
          yPosition = 30;
          addHeader();
        }
        pdf.setFont("Poppins", "bold");

        // Increase x position to give more space for the left label
        pdf.text(label + ":", 10, yPosition); // Adjust x value (10 for more space)

        pdf.setFont("Poppins", "normal");
        pdf.text(value, 70, yPosition); // Adjust x value for the value text
        yPosition += 8;
      });
      yPosition += 10;
    };

    addHeader();
    addTitle("Candidate Profile", 22, true);

    addTitle("Personal Details", 16);
    addContent([
      ["Name", personalDetails?.Name],
      ["Alias Name", "Johnny"],
      ["Age", "34"],
      ["Passport Number", "32123434"],
      ["Mobile", "1234567890"],
      ["Email 1", "john.doe@email.com"],
      ["Email 2", "john.doe@email.com"],
    ]);

    addTitle("Job Details", 16);
    addContent([
      ["Degree", "BSc"],
      ["Years of Experience", "12 years 6 months"],
      ["Gap in experience", "0 years"],
      ["Working Status", "Working"],
      ["Returning Country", "Saudi Arabia"],
    ]);

    addTitle("Status Details", 16);
    addContent([
      ["Data Flow", "Completed"],
      ["Prometric Exam", "Completed"],
      ["HRD", "Completed"],
      ["Medical", "In Process"],
      ["VFS", "Pending"],
      ["Apostille", "Pending"],
      ["Travel", "Not Travelled"],
    ]);

    addTitle("Document Submission", 16);
    if (documentSubmission?.length > 0) {
      // Check if there's enough space before adding the table
      if (yPosition > 250) {
        pdf?.addPage(); // Add a new page if necessary
        yPosition = 30;
        addHeader();
      }

      pdf?.autoTable({
        startY: yPosition,
        head: [["Title", "Date", "Status"]],
        body: documentSubmission.map((item) => [
          item.title,
          item.date,
          item.status,
        ]),
        theme: "grid",
        margin: { left: 15, right: 15 },
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [0, 122, 204], textColor: 255, fontSize: 12 },
        showHead: "firstPage", // FIX: Table header only on first page
      });
      yPosition = pdf.lastAutoTable.finalY + 15;
    } else {
      addContent([["No documents submitted", ""]]);
    }

    addTitle("Data Flow", 16);
    addContent([
      ["Start Date", "2025-01-10"],
      ["Complete Date", "2025-02-01"],
      ["Note", "All data processed successfully."],
      ["Report Profile", "Completed"],
    ]);

    addTitle("HRD", 16);
    addContent([
      ["Start Date", "2025-02-05"],
      ["Sent To", "HRD Department"],
      ["Completed Date", "2025-02-10"],
      ["Note", "Documents sent for verification."],
    ]);
    addTitle("Mumaris", 16);
    addContent([
      ["Start Date", "2025-02-05"],
      ["Completed Date", "2025-02-10"],
      ["Mumaris ID", "123123"],
      ["Mumaris Password", "123123"],
      ["Note", "Documents sent for verification."],
    ]);
    addTitle("Prometric Exam", 16);
    addContent([
      ["Exam Date", "2025-02-05"],
      ["License Card", "XXXXXXX"],
      ["Booked Date", "2025-02-05"],
      ["Attempt Date 1", "2025-02-05"],
      ["Attempt Date 2", "2025-02-05"],
      ["Attempt Date 3", "2025-02-05"],
      ["Note", "Documents sent for verification."],
    ]);
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      addFooter(i, totalPages);
    }

    pdf.save("Candidate_Profile.pdf");
  };

  const [openDialog, setOpenDialog] = useState(false);
  const [zipPassword, setZipPassword] = useState("");
  const [error, setError] = useState(false);

  const handleOpenDialog = () => {
    setZipPassword("");
    setError(false);
    setOpenDialog(true);
  };

  configure({ useWebWorkers: false });

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const { id } = useParams();
  const decryptedId = decryptId(decodeURIComponent(id));

  const [decryptedJAId, setdecryptedJAId] = useState(null);
  // onclicking a particular job from applied job list..

  const location = useLocation();
  const navigate = useNavigate();

  const paginationState = location?.state?.pagination || {
    page: 0,
    rowsPerPage: 25,
  };

  const filterData = location?.state?.filterData || {
    name: "",
    passport_no: "",
    profession: "",
    CountryID: "",
    job: "",
    jobStatus: "",
    start_date: "",
    end_date: "",
    branch: "",
    recruitmentProcedure: null,
    recruitmentStages: null,
    recruitmentSubProcedures: null,
    recruitmentProcedureFields: null,
  };

  const expandedIds = location?.state?.expandedIds || "";
  const open = location?.state?.open || "";

  const isMobile = useMediaQuery("(max-width: 1220px)");

  const [selectedProfileTab, setSelectedProfileTab] = useState(0);

  const [candidateID, setCandidateID] = useState("");

  const [personalDetails, setPersonalDetails] = useState({
    Name: "",
    Alias_Name: "",
    Date_Of_Birth: "",
    Passport_Number: "",
    Mobile: "",
    Emergency_Mobile: "",
    Primary_Email: "",
    Secondary_Email: "",
    Address: "",
    CI_Photo: "",
  });

  const [jobDetails, setJobDetails] = useState({
    Job_Title: "",
    Designation: "",
    Is_Medical: "",
    Years_Of_Experience: "",
    Gap_In_Experience: "",
    Gap_Details: "",
    Working_Status: "",
    Department_1: "",
    Department_2: "",
    Department_3: "",
    Designation: "",
    License_Info: "",
    License_Expiry: "",
    Employer: "",
    Contract_Type: "",
    Work_Location: "",
    Accommodation_Provided: "",
    Transportation_Provided: "",
    Basic_Salary: "",
    Other_Benefits: "",
  });

  const [appliedJobs, setAppliedJobs] = useState([]);

  const [statusList, setStatusList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");

  const [countryID, setCountryID] = useState(null);
  const [professionID, setProfessionID] = useState(null);
  const [recruitmentProfession, setRP] = useState(null);
  const [recruitmentCountry, setRC] = useState(null);

  const [selectedJobId, setSelectedJobId] = useState("");

  const [selectedJobName, setSelectedJobName] = useState("");
  const [selectedJobID, setSelectedJobID] = useState("");

  const handleChange = (event) => {
    const selected = appliedJobs.find(
      (job) => job.JD_JobId === event.target.value
    );
    setSelectedJobId(event.target.value);

    if (selected) {
      setCountryID(selected.JD_Country);
      setProfessionID(selected.JD_Profession);
      setRP(selected.Recruitment_Profession);
      setRC(selected.Recruitment_Country);
      setDeployed(selected.JA_Isdeployed);
      setCancelled(selected?.JD_Cancelled);
      setdecryptedJAId(selected?.JobApplicationId);
      setSelectedJobName(selected?.JD_Title);
      setSelectedJobID(selected?.JD_JobId);
    }
  };

  const handleStatusChange = (job, e) => {
    axiosAPI
      ?.post(`/Candidate/updateInterviewStatus/${decryptedJAId}`, {
        JA_CandidateId: candidateID,
        JA_JobId: job?.JobId,
        JA_StatusId: e?.target.value,
      })
      .then((res) => {
        if (res?.data?.success) {
          successNotify(res?.data?.data?.message);
          getData();
        } else {
          errorNotify(res?.data?.data?.message);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const [openSelectId, setOpenSelectId] = useState(null);
  const [visibility, setvisibility] = useState(1);

  // decryptedId & decryptedJAId
  const getData = async () => {
    await axiosAPI
      .get(`/Candidate/listbyIdForView/${decryptedId}`)
      .then((res) => {
        const dataFromApi = res.data.data;

        setCandidateID(dataFromApi?.CI_Id);
        setAppliedJobs(dataFromApi?.jobs);

        let selected = dataFromApi?.jobs ? dataFromApi?.jobs[0] : "";

        if (selected) {
          setSelectedJobId(selected.JD_JobId);
          setCountryID(selected.JD_Country);
          setProfessionID(selected.JD_Profession);
          setRP(selected.Recruitment_Profession);
          setRC(selected.Recruitment_Country);
          setDeployed(selected.JA_Isdeployed);
          setCancelled(selected?.JD_Cancelled);
          setCancelRemarkPreview(selected?.JD_CancelRemarks);
          setdecryptedJAId(selected?.JobApplicationId);
          setSelectedJobName(selected?.JD_Title);
          setSelectedJobID(selected?.JD_JobId);
          setOpenSelectId(selected?.JD_JobId);
          setSelectedStatus(selected?.JASL_Id);
        }

        setPortalAccess(dataFromApi?.CI_PortalAccess);

        setPersonalDetails({
          Name: dataFromApi?.CI_Name || "-",
          Alias_Name: dataFromApi?.CI_Alias || "-",
          Age: dataFromApi?.CI_Age > 0 ? dataFromApi?.CI_Age : "-" || "-",
          Date_Of_Birth:
            dataFromApi?.CI_Dateofbirth &&
            dataFromApi?.CI_Dateofbirth !== "00-00-0000"
              ? dataFromApi.CI_Dateofbirth
              : "-",
          Passport_Number: dataFromApi?.CI_PassportNo || "-",
          Mobile: dataFromApi?.CI_PrimaryMobile || "-",
          Emergency_Mobile: dataFromApi?.CI_EmergencyMobile || "-",
          Primary_Email: dataFromApi?.CI_PrimaryEmail || "-",
          Secondary_Email: dataFromApi?.CI_SecondaryEmail || "-",
          Address: dataFromApi?.CI_Address || "-",
          CI_Photo: dataFromApi?.CI_Photo,
        });

        setJobDetails({
          Job_Title: dataFromApi?.CI_Profession || "-",
          Designation: dataFromApi?.CI_Designation || "-",
          Is_Medical: dataFromApi?.CI_IsMedical == 1 ? "Yes" : "No",
          Years_Of_Experience: dataFromApi?.CI_YearofExp || "-",
          Gap_In_Experience: dataFromApi?.CI_CareerGap?.toString() || "-",
          Gap_Details: dataFromApi?.CI_CareerGapDetl || "-",
          Working_Status:
            dataFromApi?.CI_Last_working_date &&
            dataFromApi?.CI_Last_working_date != "00-00-0000"
              ? `Last Worked: ${dataFromApi?.CI_Last_working_date}`
              : "Working",
          Department_1: dataFromApi?.CI_Department1 || "-",
          Department_2: dataFromApi?.CI_Department2 || "-",
          Department_3: dataFromApi?.CI_Department3 || "-",
          Designation: dataFromApi?.CI_Designation || "-",
          License_Info: dataFromApi?.CI_Registration_License || "-",
          License_Expiry: dataFromApi?.CI_Registration_License_expry || "-",
          // 'Employer': "",
          // "Contract Type": "",
          // "Work Location": "",
          // "Accommodation Provided": "",
          // "Transportation Provided": "",
          // "Basic Salary": "",
          // "Other Benefits": "",
        });
      })
      .catch((err) => {
        console.log(err, "err");
      });
  };

  const getStatus = async () => {
    try {
      const res = await axiosAPI.get(`/JobApplicationStatus/list`);
      setStatusList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // mainAPICallback -> decryptedId & decryptedJAId
  const fetchProcessStatus = () => {
    axiosAPI
      .get(`/RecruitmentProcedure/recruitment/${decryptedId}/${decryptedJAId}`)
      .then((res) => {
        const { CandidateField, RPM_Id } = res?.data?.data || {};
        setrpmID(RPM_Id);

        if (CandidateField) {
          setOriginalValue2(CandidateField);
        }
      })
      .catch((error) => {
        console.error("Error fetching process status:", error);
      });
  };

  // decryptedId & decryptedJAId
  const fetchProcessData = () => {
    setLoading(true);

    axiosAPI
      .get(`/RecruitmentProcedure/recruitment/${decryptedId}/${decryptedJAId}`)
      .then((res) => {
        const { CandidateField, RPM_Id } = res?.data?.data || {};

        setrpmID(RPM_Id);

        if (CandidateField) {
          setOriginalValue(CandidateField);
          const dataWithInitializedValues = JSON.parse(
            JSON.stringify(CandidateField)
          );

          dataWithInitializedValues.stages.forEach((stage) => {
            if (stage.fields) {
              stage.fields.forEach((field) => {
                if (!field.hasOwnProperty("RPF_Value")) {
                  if (field.RPF_Type === "Checkbox") {
                    field.RPF_Value = false;
                  } else if (field.RPF_Type === "Number") {
                    field.RPF_Value = null;
                  } else {
                    field.RPF_Value = "";
                  }
                }
              });
            }

            if (stage.subProcedures) {
              stage.subProcedures.forEach((sub) => {
                sub.fields.forEach((field) => {
                  if (!field.hasOwnProperty("RPF_Value")) {
                    if (field.RPF_Type === "Checkbox") {
                      field.RPF_Value = false;
                    } else if (field.RPF_Type === "Number") {
                      field.RPF_Value = null;
                    } else {
                      field.RPF_Value = "";
                    }
                  }
                });
              });
            }
          });

          setCurrentProcessFlowData(dataWithInitializedValues);
        } else {
          setErrorParent("No process flow is associated with this candidate.");
        }
      })
      .catch((error) => {
        console.error("Error fetching process data:", error);
        setErrorParent("An error occurred while fetching process data.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const [loading, setLoading] = useState(true);
  const [errorParent, setErrorParent] = useState(null);

  const [rpmID, setrpmID] = useState(null);

  const [originalValue, setOriginalValue] = useState([]);
  const [originalValue2, setOriginalValue2] = useState([]);
  const [currentProcessFlowData, setCurrentProcessFlowData] = useState(null);

  const [mainAPICallback, setMainAPICallback] = useState(false);

  const [portalAccess, setPortalAccess] = useState(0);
  const [deployed, setDeployed] = useState(0);
  const [cancelled, setCancelled] = useState(0); // implement as needed

  const [confirmOpen, setConfirmOpen] = useState(false);

  const [pendingChange, setPendingChange] = useState(null);

  const [cancelRemark, setCancelRemark] = useState("");

  const [cancelRemarkPreview, setCancelRemarkPreview] = useState("");

  const handleCheckboxClick = (type, value) => {
    setPendingChange({ type, value });
    setConfirmOpen(true);
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setCancelRemark(""); // clear on close
    setPendingChange(""); // optional
  };

  const handleConfirm = async () => {
    if (!pendingChange) return;

    const { type, value } = pendingChange;

    try {
      if (type === "Web Portal Access") {
        setPortalAccess(value ? 1 : 0);

        const res = await axiosAPI.post("/Candidate/updateportalaccess", {
          CandidateID: decryptedId,
          IsAccess: value ? 1 : 0,
        });
        if (res?.data?.success) {
          successNotify(res?.data?.data?.message);
        }
      }

      if (type === "Deployed") {
        setDeployed(value ? 1 : 0);

        const currentDate = new Date().toISOString().split("T")[0];
        const res = await axiosAPI.post("/Candidate/updatedeploymentstatus", {
          JA_Isdeployed: value ? 1 : 0,
          JA_deployedDate: currentDate,
          JA_Id: decryptedJAId,
          JA_CandidateId: decryptedId,
        });
        if (res?.data?.success) {
          successNotify(res?.data?.message);
        } else {
          infoNotify(res?.data?.message);
          setDeployed(0);
        }
      }

      if (type === "Cancelled") {
        setCancelled(value ? 1 : 0);

        const currentDate = new Date().toISOString().split("T")[0];
        const res = await axiosAPI.post("/Candidate/updatecancelstatus", {
          JA_IsCancelled: value ? 1 : 0,
          JA_deployedDate: currentDate,
          JA_CancelRemarks: cancelRemark,
          JA_Id: decryptedJAId,
          JA_CandidateId: decryptedId,
        });
        if (res?.data?.success) {
          successNotify(res?.data?.message);
          setCancelRemarkPreview(cancelRemark);
        } else {
          infoNotify(res?.data?.message);
          setCancelled(0);
        }
      }
    } catch (error) {
      console.error(`Failed to update ${type}:`, error);
      // rollback if needed
      if (type === "Web Portal Access") setPortalAccess(0);
      if (type === "Deployed") setDeployed(0);
      if (type === "Cancelled") setCancelled(0);
    }

    setConfirmOpen(false);
    setPendingChange(null);
  };

  useEffect(() => {
    if (decryptedId && decryptedJAId) {
      fetchProcessData();
    }
  }, [decryptedId, decryptedJAId]);

  useEffect(() => {
    if (decryptedId && decryptedJAId) {
      fetchProcessStatus();
    }
  }, [mainAPICallback, decryptedJAId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
    getStatus();
  }, []);

  const stateToSend = {
    decryptedId: decryptedId,
    Tab: 2,
  };

  const navigatetoJobLinkingAdd = () => {
    const encrypted = encodeURIComponent(encryptId(decryptedId));
    navigate(`/JobLinking/Add/${encrypted}`, { state: stateToSend });
  };

  const navigatetoJobLinkingEdit = (id) => {
    const encrypted = encodeURIComponent(encryptId(id?.JobApplicationId));
    navigate(`/JobLinking/Edit/${encrypted}`, { state: stateToSend });
  };

  const [expanded, setExpanded] = useState(true); // Accordion state

  const handleAccordionToggle = () => {
    setExpanded((prev) => !prev);
  };

  const redGridRef = useRef(null);
  const [redGridHeight, setRedGridHeight] = useState(0);

  useLayoutEffect(() => {
    if (redGridRef.current) {
      setRedGridHeight(redGridRef.current.offsetHeight);
    }
  }, [selectedProfileTab, personalDetails, jobDetails, appliedJobs]);

  useEffect(() => {
    if (location?.state?.Tab) {
      setSelectedProfileTab(location?.state?.Tab);
    }
  }, [location?.state]);

  return (
    <div
      id="candidateProfile"
      style={{
        padding: "10px 30px",
        margin: "auto",
        maxWidth: isMobile ? "100vw" : `calc(100vw - 280px)`,
      }}
    >
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        {/* Left Side: Back Button + Heading */}
        <Grid item xs={12} md="auto">
          <Box display="flex" alignItems="center">
            <CommonButton
              variant="outlined"
              background="#006CB8"
              onClick={() =>
                navigate("/Candidates", {
                  state: {
                    pagination: paginationState,
                    filterData: filterData,
                    expandedIds: expandedIds,
                    open: open,
                  },
                })
              }
              margin="0px 20px 10px 0px"
            >
              <ArrowBackIcon sx={{ color: "white" }} />
            </CommonButton>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "24px",
                fontFamily: "Geist, sans-serif",
              }}
            >
              Candidate Profile
            </Typography>
          </Box>
        </Grid>

        {/* Right Side: Portal Access + Export */}
        <Grid
          item
          xs={12}
          md="auto"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            marginLeft: "auto",
            flexWrap: "wrap", // ensures responsiveness on smaller screens
          }}
        >
          <Box
            sx={{
              border: "1px solid #006CB8",
              borderRadius: 2,
              pl: 2,
              // py: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Geist, sans-serif",
                letterSpacing: "0.5px",
                fontSize: "14px",
                color: "#333",
                fontWeight: 600,
              }}
            >
              Web Portal Access
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={portalAccess === 1}
                  onChange={(e) =>
                    handleCheckboxClick("Web Portal Access", e.target.checked)
                  }
                  color="primary"
                />
              }
              label="" // no label since it's part of Typography
              sx={{ ml: 0.5 }}
            />
          </Box>

          <Button
            variant="contained"
            sx={{
              fontFamily: "Geist, sans-serif",
              bgcolor: "#009132",
              color: "white",
              textTransform: "capitalize",
              borderRadius: 2,
              whiteSpace: "nowrap",
              height: "100%", // aligns with box height
            }}
            onClick={handleOpenDialog}
            endIcon={<GetAppIcon />}
          >
            Export
          </Button>
        </Grid>
      </Grid>

      <br />

      {/* Main Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column", // stack on mobile
            md: "row", // side-by-side on desktop
          },
          alignItems: "stretch",
          gap: 2,
          width: "100%",
        }}
      >
        {/* Left Side - Personal Details & Job Details */}
        <Box
          sx={{
            width: {
              xs: "100%", // full width on small screens
              sm: "100%", // side-by-side on larger screens
              md:
                !originalValue?.stages?.length || cancelled === 1
                  ? "100%"
                  : "60%",
              lg:
                !originalValue?.stages?.length || cancelled === 1
                  ? "100%"
                  : "70%",
              xl:
                !originalValue?.stages?.length || cancelled === 1
                  ? "100%"
                  : "75%",
            },
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            sx={{ background: "#006CB8", border: "1px solid lightgrey" }}
          >
            {/* Tabs aligned to left */}
            <Tabs
              value={selectedProfileTab}
              onChange={(e, value) => {
                setSelectedProfileTab(value);
              }}
              indicatorColor="error"
              textColor="error"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                fontFamily: "Geist, sans-serif",
                "& .MuiTabs-flexContainer": {
                  justifyContent: "flex-start",
                },
              }}
            >
              {["Personal Details", "Experience Details", "Applied Jobs"].map(
                (label, index) => (
                  <Tab
                    key={label}
                    label={label}
                    sx={{
                      fontFamily: "Geist, sans-serif",
                      background:
                        selectedProfileTab !== index ? "#006CB8" : "white",
                      border: "1px solid lightgrey",
                      borderBottom: "4px solid #EAB308",
                      color: selectedProfileTab !== index ? "white" : "black",
                      fontWeight: 500,
                      fontSize: `14px`,
                      letterSpacing: "1px",
                      minWidth: "150px",
                      minHeight: "60px",
                    }}
                  />
                )
              )}
            </Tabs>
          </Box>
          <Grid
            container
            spacing={2}
            ref={redGridRef}
            // sx={{border: "2px solid red"}}
          >
            {/* Personal Details Card */}
            {selectedProfileTab == 0 && (
              <>
                <Grid item xs={12} id="personalDetailsSection">
                  <Box
                    sx={{
                      border: "1px solid lightgrey",
                      p: 3,
                    }}
                  >
                    <Grid container spacing={2}>
                      {personalDetails?.CI_Photo && (
                        <Grid
                          container
                          spacing={2}
                          item
                          xs={12}
                          sm={12}
                          md={12}
                          lg={3}
                          xl={2.5}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                            minHeight: "100%", // or a fixed height like '300px' if needed
                          }}
                        >
                          {personalDetails?.CI_Photo && (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              flexDirection="column"
                            >
                              <Avatar
                                src={`${process.env.REACT_APP_BASE_URL}/${personalDetails?.CI_Photo}`}
                                alt="Candidate"
                                sx={{ width: 150, height: 150, mb: 1 }}
                              />
                            </Box>
                          )}
                        </Grid>
                      )}

                      <Grid
                        container
                        spacing={2}
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={personalDetails?.CI_Photo ? 9 : 12}
                        xl={personalDetails?.CI_Photo ? 9.5 : 12}
                      >
                        {Object.entries(personalDetails)
                          .filter(([key]) => key !== "CI_Photo")
                          .map(([key, value]) => (
                            <Grid
                              item
                              xs={key === "Address" ? 12 : 12}
                              sm={
                                key === "Address"
                                  ? 12
                                  : originalValue?.stages?.length
                                  ? 12
                                  : 6
                              }
                              md={
                                key === "Address"
                                  ? 12
                                  : originalValue?.stages?.length
                                  ? 6
                                  : 4
                              }
                              lg={
                                key === "Address"
                                  ? 12
                                  : originalValue?.stages?.length
                                  ? 6
                                  : 4
                              }
                              xl={
                                key === "Address"
                                  ? 12
                                  : originalValue?.stages?.length
                                  ? 4
                                  : 3
                              }
                              my={1}
                              key={key}
                            >
                              <Box>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "18px",
                                    mb: 0.5,
                                  }}
                                >
                                  {key.replace(/_/g, " ")}
                                </Typography>
                                {value ? (
                                  <Typography
                                    sx={{ fontWeight: 400, fontSize: "16px" }}
                                  >
                                    {value}
                                  </Typography>
                                ) : (
                                  <Skeleton
                                    variant="text"
                                    width="80%"
                                    height={40}
                                  />
                                )}
                              </Box>
                            </Grid>
                          ))}
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </>
            )}

            {/* Job Details Card */}
            {selectedProfileTab == 1 && (
              <Grid item xs={12} id="jobDetailsSection">
                <Box
                  sx={{
                    border: "1px solid lightgrey",
                    p: 3,
                  }}
                >
                  <Grid container spacing={2}>
                    {Object.entries(jobDetails).map(([key, value]) => (
                      <Grid
                        item
                        xs={12}
                        sm={originalValue?.stages?.length ? 12 : 6}
                        md={originalValue?.stages?.length ? 6 : 4}
                        lg={originalValue?.stages?.length ? 6 : 4}
                        xl={originalValue?.stages?.length ? 4 : 3}
                        key={key}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontFamily: "Geist, sans-serif",
                              fontSize: "18px",
                              mb: 0.5,
                            }}
                          >
                            {key.replace(/_/g, " ")}
                          </Typography>
                          {value ? (
                            <Typography
                              sx={{
                                fontWeight: 400,
                                fontFamily: "Geist, sans-serif",
                                fontSize: "16px",
                              }}
                            >
                              {value}
                            </Typography>
                          ) : (
                            <Skeleton variant="text" width="80%" height={40} />
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
            )}

            {/* Applied Jobs */}
            {selectedProfileTab == 2 && (
              <Grid item xs={12} id="jobDetailsSection">
                <Box>
                  <Accordion
                    expanded={expanded}
                    elevation={0}
                    sx={{
                      background: "white",
                      border: "1px solid lightgrey",
                      p: 2,
                    }}
                  >
                    <AccordionSummary
                      aria-controls="applied-jobs-content"
                      id="applied-jobs-header"
                      sx={{ p: 0, mb: 2, background: "white" }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                      >
                        <Typography
                          sx={{
                            fontFamily: "Geist, sans-serif",
                            fontWeight: `700`,
                            fontStyle: `Bold`,
                            fontSize: `20px`,
                            leadingTrim: `NONE`,
                            lineHeight: `100%`,
                            letterSpacing: `0%`,
                            fontSize: "24px",
                          }}
                          px={2}
                        >
                          Applied Jobs
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={navigatetoJobLinkingAdd}
                          >
                            Add
                          </Button>
                          <IconButton
                            onClick={handleAccordionToggle}
                            sx={{
                              border: "2px solid lightgrey",
                              // transform: expanded ? "rotate(180deg)": "rotate(0deg)",
                              transform: expanded ? "scaleY(-1)" : "scaleY(1)",
                              transition: "transform 0.5s ease",
                            }}
                          >
                            <ExpandMore />
                          </IconButton>
                        </Box>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails
                      sx={{ p: 0, background: "white", overflowX: "auto" }}
                    >
                      <Grid container>
                        <FormControl
                          component="fieldset"
                          sx={{ width: "100%" }}
                        >
                          <RadioGroup
                            value={selectedJobId}
                            onChange={handleChange}
                          >
                            <Box sx={{ minWidth: "850px" }}>
                              {/* Header Row */}
                              <Grid
                                container
                                sx={{
                                  backgroundColor: "#006CB8",
                                  color: "white",
                                  p: 2,
                                  borderRadius: "10px 10px 0px 0px",
                                }}
                              >
                                <Grid item sx={{ textAlign: "left" }} xs={4}>
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight="600"
                                  >
                                    Job Title
                                  </Typography>
                                </Grid>
                                <Grid item sx={{ textAlign: "center" }} xs={4}>
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight="600"
                                  >
                                    Interview Status
                                  </Typography>
                                </Grid>
                                <Grid item sx={{ textAlign: "center" }} xs={4}>
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight="600"
                                  >
                                    Action
                                  </Typography>
                                </Grid>
                              </Grid>

                              {/* Job Rows */}
                              {appliedJobs?.length > 0 ? (
                                appliedJobs.map((job) => (
                                  <Grid
                                    container
                                    key={job.JD_JobId}
                                    alignItems="center"
                                    sx={{
                                      border:
                                        selectedJobId == job?.JD_JobId
                                          ? "2px solid #006CB8"
                                          : "1px solid lightgrey",
                                      background:
                                        selectedJobId == job?.JD_JobId
                                          ? `#006CB80A`
                                          : "",
                                    }}
                                  >
                                    {/* Job Title with Radio */}
                                    <Grid item xs={4}>
                                      <Paper
                                        variant="outlined"
                                        sx={{
                                          p: 1,
                                          borderRadius: "0px",
                                          border: "0px solid transparent",
                                          background:
                                            selectedJobId == job?.JD_JobId
                                              ? `#006CB80A`
                                              : "",
                                        }}
                                      >
                                        <FormControlLabel
                                          value={job.JD_JobId}
                                          control={<Radio />}
                                          label={
                                            <Typography variant="body1">
                                              {job.JD_Title}
                                            </Typography>
                                          }
                                        />
                                      </Paper>
                                    </Grid>

                                    {/* Interview Status Dropdown */}
                                    <Grid item xs={4}>
                                      <Paper
                                        sx={{
                                          p: 1,
                                          borderRadius: "0px",
                                          border: "0px solid transparent",
                                          boxShadow: "none",
                                          background:
                                            selectedJobId == job?.JD_JobId
                                              ? `#006CB80A`
                                              : "",
                                        }}
                                      >
                                        <FormControl
                                          fullWidth
                                          size="small"
                                          sx={{
                                            border: "0px solid transparent",
                                            boxShadow: "none",
                                            zIndex:
                                              openSelectId != job?.JD_JobId
                                                ? 0
                                                : 1,
                                          }}
                                          onClick={() => {
                                            setOpenSelectId(job?.JD_JobId);
                                          }}
                                        >
                                          <SelectInput
                                            value={job?.JASL_Id}
                                            name="jobapplication"
                                            onChange={(e) => {
                                              handleStatusChange(job, e);
                                              setSelectedStatus(e.target.value);
                                            }}
                                            options={statusList?.map(
                                              (item) => ({
                                                value: item?.JASL_Id,
                                                label: item?.JASL_Name,
                                              })
                                            )}
                                            placeholder="Select Status"
                                            onMenuClose={() => {
                                              setvisibility(1);
                                              setOpenSelectId("");
                                            }}
                                            onMenuOpen={() => {
                                              setvisibility(0);
                                            }}
                                          />
                                        </FormControl>
                                      </Paper>
                                    </Grid>

                                    {/* Edit Button */}
                                    <Grid item xs={4}>
                                      <Paper
                                        variant="outlined"
                                        sx={{
                                          p: 1,
                                          display: "flex",
                                          justifyContent: "center",
                                          borderRadius: "0px",
                                          border: "0px solid transparent",
                                          background:
                                            selectedJobId == job?.JD_JobId
                                              ? `#006CB80A`
                                              : "",
                                        }}
                                      >
                                        <IconButton
                                          onClick={() =>
                                            navigatetoJobLinkingEdit(job)
                                          }
                                          sx={{
                                            color: "#006CB8",
                                            backgroundColor: "white",
                                            border: "1px solid #006CB8",
                                            "&:hover": {
                                              backgroundColor: "#e0f0ff",
                                            },
                                          }}
                                          size="small"
                                        >
                                          <Edit />
                                        </IconButton>
                                      </Paper>
                                    </Grid>
                                  </Grid>
                                ))
                              ) : (
                                <Box
                                  height={150}
                                  display="flex"
                                  justifyContent="center"
                                  alignItems="center"
                                  sx={{
                                    color: "grey",
                                    fontSize: "28px",
                                  }}
                                >
                                  No data to show
                                </Box>
                              )}
                            </Box>
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </Grid>
            )}

            {/* Recruitment Procedure Details */}
            {recruitmentProfession && recruitmentCountry && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: "1px solid lightgrey",
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#006CB81F",
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontFamily: "Geist, sans-serif",
                      }}
                    >
                      Recruitment Procedure Details
                    </Typography>
                  </Box>

                  <Grid container spacing={2} p={2}>
                    {/* Left Side: Country, Profession, Job */}
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={8}>
                      <Box
                        sx={{
                          border: "2px solid #006CB8",
                          height: "100%",
                          padding: 2,
                          borderRadius: 2,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-evenly",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontFamily: "Geist, sans-serif",
                              fontSize: "18px",
                              mb: 0.5,
                              minWidth: "250px",
                            }}
                          >
                            Destination Country
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontFamily: "Geist, sans-serif",
                              fontSize: "16px",

                              minWidth: "250px",
                            }}
                          >
                            {recruitmentCountry || "-"}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontFamily: "Geist, sans-serif",
                              fontSize: "18px",
                              mb: 0.5,
                              minWidth: "250px",
                            }}
                          >
                            Recruitment Profession
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontFamily: "Geist, sans-serif",
                              fontSize: "16px",

                              minWidth: "250px",
                            }}
                          >
                            {recruitmentProfession || "-"}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontFamily: "Geist, sans-serif",
                              fontSize: "18px",
                              mb: 0.5,
                              minWidth: "250px",
                            }}
                          >
                            Recruitment Job
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontFamily: "Geist, sans-serif",
                              fontSize: "16px",

                              minWidth: "250px",
                            }}
                          >
                            {selectedJobName
                              ? `${selectedJobName} ${
                                  selectedJobID ? `(${selectedJobID})` : ""
                                }`
                              : "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Right Side: Deployed & Cancelled Checkboxes */}
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={4}>
                      <Box
                        sx={{
                          border: "2px solid #006CB8",
                          height: "100%",
                          padding: 2,
                          borderRadius: 2,
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {/* Deployed Box */}
                        <Box
                          sx={{
                            border: "1px solid #ccc",
                            borderRadius: 1,
                            padding: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Geist, sans-serif",
                              fontSize: "16px",
                              color: "#333",
                              fontWeight: 500,
                            }}
                          >
                            Deployed
                          </Typography>
                          <Checkbox
                            checked={deployed === 1}
                            onChange={(e) =>
                              handleCheckboxClick("Deployed", e.target.checked)
                            }
                            disabled={cancelled == 1 ? true : false}
                            color="primary"
                          />
                        </Box>

                        {/* Cancelled Box */}
                        <Box
                          sx={{
                            border: "1px solid #ccc",
                            borderRadius: 1,
                            padding: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Geist, sans-serif",
                              fontSize: "16px",
                              color: "#333",
                              fontWeight: 500,
                            }}
                          >
                            Cancelled
                          </Typography>
                          <Checkbox
                            checked={cancelled === 1}
                            onChange={(e) =>
                              handleCheckboxClick("Cancelled", e.target.checked)
                            }
                            disabled={deployed == 1 ? true : false}
                            color="primary"
                          />
                        </Box>

                        {cancelled == 1 && cancelRemarkPreview && (
                          <Typography
                            sx={{
                              fontFamily: "Geist, sans-serif",
                              fontSize: "16px",
                              color: "#333",
                              fontWeight: 500,
                            }}
                          >
                            Remarks : {cancelRemarkPreview}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {cancelled != 1 && originalValue?.stages?.length > 0 && (
          <Box
            sx={{
              // border: "2px solid blue",
              overflowY: "auto",
              maxHeight: `${redGridHeight + 46}px`,
              width: {
                xs: "100%", // full width below red box on small screens
                sm: "100%", // side-by-side on desktop
                md: "40%",
                lg: "30%",
                xl: "25%",
              },
            }}
            id="statusDetailsSection"
          >
            <Card
              sx={{
                height: "100%",
                overflowY: "auto",
                border: "1px solid lightgrey",
                boxShadow: "none",
                borderRadius: `0px`,
              }}
            >
              <Box>
                <Typography
                  fontWeight={600}
                  mb={2}
                  p={2.1}
                  sx={{
                    minHeight: "61px",
                    background: "#006CB8",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "16px",
                    color: "white",
                  }}
                >
                  Status of {selectedJobName}
                </Typography>

                {originalValue2?.stages?.map((stage, index) => {
                  const hasSubProcedures =
                    Array.isArray(stage.subProcedures) &&
                    stage.subProcedures.length > 0;

                  let stageStatus = "0"; // default: in progress

                  const subComponents = hasSubProcedures
                    ? stage.subProcedures.map((sub) => {
                        const fields = sub?.fields || [];
                        const statusField = fields.find(
                          (field) => field?.RPF_Type === "IsCompleted"
                        );
                        const status =
                          [statusField?.RPF_Value].flat()[0] || "0";
                        return {
                          ...sub,
                          isCompleted: status,
                        };
                      })
                    : [];

                  if (hasSubProcedures) {
                    const statuses = subComponents.map((s) => s.isCompleted);
                    if (statuses.every((s) => s === "1")) {
                      stageStatus = "1";
                    } else if (statuses.every((s) => s === "2")) {
                      stageStatus = "2";
                    } else {
                      stageStatus = "0";
                    }
                  } else {
                    const stageField = stage?.fields?.find(
                      (field) => field?.RPF_Type === "IsCompleted"
                    );
                    stageStatus = [stageField?.RPF_Value].flat()[0] || "0";
                  }
                  // Color code for sidebar
                  const getStageColor = (status) => {
                    switch (status) {
                      case "1":
                        return {
                          border: "#22C55E", //Green
                          bg: "#22C55E0D",
                          label: "Completed",
                        };
                      case "2":
                        return {
                          border: "#9CA3AF", // Greyish (Tailwind's gray-400)
                          bg: "#9CA3AF0D",
                          label: "Draft",
                        };
                      default:
                        return {
                          border: "#006CB8", // Blue
                          bg: "#EBF2FF",
                          label: "In Progress",
                        };
                    }
                  };

                  const subStatus = stageStatus;
                  const stageColor = getStageColor(stageStatus);

                  const allFields = [
                    ...(stage?.fields || []),
                    ...(stage?.subProcedures || []).flatMap(
                      (sub) => sub?.fields || []
                    ),
                  ];

                  const stageStatusField = allFields.find((field) =>
                    field?.RPF_Name?.trim()?.toLowerCase().includes("status")
                  );

                  return (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        backgroundColor: stageColor.bg,
                        borderLeft: `5px solid ${stageColor.border}`,
                        boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
                        margin: "15px",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        {getIcon(
                          subStatus == "1"
                            ? "Completed"
                            : stageColor.label == "2"
                            ? "Draft"
                            : "In Process",
                          stageColor.border
                        )}
                        <Box>
                          <Typography fontWeight="bold">
                            {stage.RPS_Name}
                          </Typography>
                          {stageStatusField && (
                            <Typography
                              sx={{ color: stageColor.border }}
                              fontSize={14}
                            >
                              {Array.isArray(stageStatusField.RPF_Value)
                                ? stageStatusField.RPF_Value.join(", ")
                                : stageStatusField.RPF_Value || ""}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {hasSubProcedures &&
                        subComponents.map((sub, subIndex) => {
                          const subStatus = sub.isCompleted;
                          const subColor = getStageColor(subStatus);
                          const subStatusField = (sub?.fields || []).find(
                            (field) =>
                              field?.RPF_Name?.trim()
                                ?.toLowerCase()
                                .includes("status")
                          );

                          return (
                            <Box
                              key={`sub-${subIndex}`}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mt: 1.5,
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: subColor.bg,
                                borderLeft: `4px solid ${subColor.border}`,
                              }}
                            >
                              {getIcon(
                                subStatus == "1"
                                  ? "Completed"
                                  : subStatus == "2"
                                  ? "Draft"
                                  : "In Process",
                                subColor.border
                              )}
                              <Box>
                                <Typography fontWeight={500}>
                                  {sub.RSP_Name}
                                </Typography>
                                {subStatusField && (
                                  <Typography
                                    sx={{ color: subColor.border }}
                                    fontSize={13}
                                  >
                                    {Array.isArray(subStatusField.RPF_Value)
                                      ? subStatusField.RPF_Value.join(", ")
                                      : subStatusField.RPF_Value || ""}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                    </Box>
                  );
                })}
              </Box>
            </Card>
          </Box>
        )}
      </Box>

      {decryptedJAId && cancelled != 1 && (
        <ProcessFlowViewer
          candidateIDOriginal={id}
          candidateID={decryptedId}
          JAID={decryptedJAId}
          countryID={countryID}
          professionID={professionID}
          rpmID={rpmID}
          originalValue={originalValue}
          currentProcessFlowDataParent={currentProcessFlowData}
          loading={loading}
          error={errorParent}
          mainAPICallback={mainAPICallback}
          setMainAPICallback={setMainAPICallback}
        />
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={handleCancel}>
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            color: "#0977BC",
            mt: 3,
            mb: 2,
            fontFamily: "Geist, sans-serif",
          }}
        >
          Confirm Action
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                mb: 1,
                fontFamily: "Geist, sans-serif",
              }}
            >
              Are you sure you want to{" "}
              {pendingChange?.value ? "enable" : "disable"}{" "}
              {pendingChange?.type} Status?
            </Typography>

            {pendingChange?.type === "Cancelled" && cancelled != 1 && (
              <TextAreaInput
                label="Cancel Remark"
                placeholder="Cancel Remark"
                value={cancelRemark}
                onChange={(e) => setCancelRemark(e.target.value)}
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ mt: 3, mb: 3, mr: 3 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <CommonButton
              variant="outlined"
              customColor="#B0BEC5"
              onClick={handleCancel}
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant="contained"
              customColor="#007BFF"
              onClick={handleConfirm}
            >
              Proceed
            </CommonButton>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CandidateProfile;
